import { ArrowUpRight, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/lib/translations";

const Footer = () => {
  const { t } = useLang();

  const scrollTo = (id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const backTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0a0a0a] border-t border-white/10 pt-24 md:pt-32 pb-12 relative"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Massive CTA */}
        <div className="mb-24 md:mb-32">
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 mb-6">
            → {t.footer.cta}
          </p>
          <button
            onClick={() => scrollTo("contact")}
            data-testid="footer-cta"
            className="group text-left block"
          >
            <h2
              className="font-display font-extrabold text-white leading-[0.85] tracking-tighter"
              style={{ fontSize: "clamp(3.5rem, 12vw, 12rem)" }}
            >
              {t.footer.ctaSub.split(" ").map((w, i) => (
                <span
                  key={i}
                  className="inline-block group-hover:text-[#FF3B30] transition-colors duration-500 mr-[0.15em]"
                >
                  {w}
                </span>
              ))}
              <ArrowUpRight
                className="inline-block ml-2 md:ml-6 text-[#FF3B30] group-hover:translate-x-2 group-hover:-translate-y-2 transition-transform duration-500"
                style={{ width: "0.7em", height: "0.7em" }}
                strokeWidth={1.2}
              />
            </h2>
          </button>
        </div>

        {/* Footer columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-white/10">
          <div className="md:col-span-5">
            <Logo size="large" />
            <p className="mt-6 text-white/50 text-sm max-w-sm leading-relaxed">
              {t.about.p1.slice(0, 140)}…
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-4">
              KONTAKT
            </p>
            <div className="space-y-2 text-sm">
              <a
                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                className="block text-white hover:text-[#FF3B30] transition-colors"
                data-testid="footer-phone"
              >
                {COMPANY.phone}
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                className="block text-white hover:text-[#FF3B30] transition-colors"
                data-testid="footer-email"
              >
                {COMPANY.email}
              </a>
            </div>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mb-4">
              STANDORT
            </p>
            <address className="not-italic text-sm text-white/80 leading-relaxed">
              {COMPANY.name}
              <br />
              {COMPANY.addressLine1}
              <br />
              {COMPANY.addressLine2}
            </address>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-mono text-[10px] tracking-[0.25em] text-white/40">
            © {new Date().getFullYear()} {COMPANY.name} — {t.footer.rights}
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/impressum"
              data-testid="footer-imprint-link"
              className="font-mono text-[10px] tracking-[0.25em] text-white/40 hover:text-white transition-colors cursor-pointer"
            >
              {t.footer.imprint}
            </Link>
            <Link
              to="/datenschutz"
              data-testid="footer-privacy-link"
              className="font-mono text-[10px] tracking-[0.25em] text-white/40 hover:text-white transition-colors cursor-pointer"
            >
              {t.footer.privacy}
            </Link>
            <button
              onClick={backTop}
              data-testid="footer-back-top"
              className="font-mono text-[10px] tracking-[0.25em] text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowUp size={12} /> {t.footer.backTop}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
